import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { routes } from '@routes';
import { JobLicenseUsageHistoryResponseDto } from 'app/API';
import { usePageContext } from '../../../../../../providers/PageProvider';
import { Divider } from '../../styles/Divider';
import { FullWidthSectionRight } from '../../styles/FullWidthSectionRight';
import { InfoBlockFullSize } from '../../styles/InfoBlockFullSize';
import { JobSection } from '../../styles/JobSection';
import { JobSectionLeft } from '../../styles/JobSectionLeft';
import { CellContent } from '../AssigneeHistorySection/styles/CellContent';
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { EntityIconCompact } from '@components/core-ui/EntityIcon/variations/EntityIconCompact';
import { AvatarBlock } from './styles/AvatarBlock';
import { CellContentFlex } from './styles/CellContentFlex';
import { MetaStack } from './styles/MetaStack';
import { StyledLink } from './styles/StyledLink';
import { SubtleMeta } from './styles/SubtleMeta';
import { SubtleMetaLink } from './styles/SubtleMetaLink';
import { UserEmail } from './styles/UserEmail';
import { UserInfo } from './styles/UserInfo';
import { UserNameLink } from './styles/UserNameLink';

export const LicenseUsageHistorySection = () => {
  const { licenseUsageHistory } = usePageContext();

  if (!licenseUsageHistory || licenseUsageHistory.length === 0) {
    return null;
  }

  const renderAvatar = (user: { name: string; lastname: string; avatar?: string | null }) => {
    if (!user || !user.name || !user.lastname) {
      return null;
    }
    return (
      <AvatarBlock>
        <EntityIconCompact name={`${user.name} ${user.lastname}`} src={user.avatar ?? undefined} />
      </AvatarBlock>
    );
  };

  const renderUser = (
    user: { id: string; name: string; lastname: string; email: string; avatar?: string | null } | null | undefined,
    licenseId?: string | null,
  ) => {
    if (!user) {
      return licenseId ? <SubtleMeta title={licenseId}>ID: {licenseId.substring(0, 8)}...</SubtleMeta> : '-';
    }
    return (
      <CellContentFlex>
        {renderAvatar(user)}
        <UserInfo>
          <UserNameLink to={routes.adminUserDetails.make(user.id)}>
            {user.name} {user.lastname}
          </UserNameLink>
          <UserEmail>{user.email}</UserEmail>
          {licenseId ? <SubtleMeta title={licenseId}>ID: {licenseId.substring(0, 8)}...</SubtleMeta> : null}
        </UserInfo>
      </CellContentFlex>
    );
  };

  return (
    <>
      <JobSection>
        <JobSectionLeft>License Usage History</JobSectionLeft>
        <FullWidthSectionRight>
          <InfoBlockFullSize>
            <TableWrapper>
              <Table style={{ width: '100%', margin: '-10px 0 0' }}>
                <Thead>
                  <Tr style={{ border: 'none' }}>
                    <Th style={{ width: '170px', padding: 0, height: '40px' }}>Date</Th>
                    <Th style={{ padding: 0, height: '40px' }}>License Assignee</Th>
                    <Th style={{ padding: 0, height: '40px' }}>Subscription</Th>
                    <Th style={{ padding: 0, height: '40px' }}>Subscription Status</Th>
                    <Th style={{ padding: 0, height: '40px' }}>Transcript Unlock Type</Th>
                    <Th style={{ padding: 0, height: '40px' }}>Actor</Th>
                    <Th style={{ padding: 0, height: '40px' }}>Event Type</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {licenseUsageHistory?.map((item: JobLicenseUsageHistoryResponseDto, index: number) => (
                    <Tr key={index}>
                      <Td>
                        <CellContent>{getAmericanTime(item.createdAt)}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          {item.Assignee?.User
                            ? renderUser(item.Assignee.User, item.License?.id || item.licenseId)
                            : renderUser(null, item.License?.id || item.licenseId)}
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <MetaStack>
                            {item.Subscription?.id ? (
                              <StyledLink to={routes.adminAutoScriptOneSubscriptionDetails.make(item.Subscription.id)}>
                                {item.Subscription.id.substring(0, 8)}...
                              </StyledLink>
                            ) : item.subscriptionId ? (
                              <StyledLink to={routes.adminAutoScriptOneSubscriptionDetails.make(item.subscriptionId)}>
                                {item.subscriptionId.substring(0, 8)}...
                              </StyledLink>
                            ) : (
                              '-'
                            )}
                            {item.SubscriptionProduct ? (
                              <SubtleMetaLink
                                to={routes.adminSubscriptions.make()}
                                title={item.SubscriptionProduct.description || item.SubscriptionProduct.id}
                              >
                                {item.SubscriptionProduct.name}
                              </SubtleMetaLink>
                            ) : item.subscriptionProductId ? (
                              <SubtleMeta title={item.subscriptionProductId}>
                                {item.subscriptionProductId.substring(0, 8)}...
                              </SubtleMeta>
                            ) : null}
                            <SubtleMeta>
                              Backup:{' '}
                              {item.License?.asOneBackupRecordingAllowed || item.asOneBackupRecordingAllowed
                                ? 'Yes'
                                : 'No'}
                            </SubtleMeta>
                          </MetaStack>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>{item.Subscription?.status || item.subscriptionStatus || '-'}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          {getTranscriptUnlockTypeName(item.License?.transcriptUnlockType || item.transcriptUnlockType)}
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>{item.Actor ? renderUser(item.Actor) : '-'}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{item.eventType}</CellContent>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>
          </InfoBlockFullSize>
        </FullWidthSectionRight>
      </JobSection>
      <Divider />
    </>
  );
};
